import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';

import * as tboApi from './tbo.api';

import type {
  TboCityListQuery,
  TboCountryListQuery,
  TboHotelListQuery,
  TboStateListQuery,
} from '@utazzo/shared';

const QK = {
  countries: (q: TboCountryListQuery) => ['tbo', 'countries', q] as const,
  states: (q: TboStateListQuery) => ['tbo', 'states', q] as const,
  cities: (q: TboCityListQuery) => ['tbo', 'cities', q] as const,
  cityOptions: (countryCode: string, q: string) => ['tbo', 'city-options', countryCode, q] as const,
  hotels: (q: TboHotelListQuery) => ['tbo', 'hotels', q] as const,
  hotelStatus: () => ['tbo', 'hotels', 'status'] as const,
};

export const useTboCountriesList = (query: TboCountryListQuery, enabled: boolean) =>
  useQuery({
    queryKey: QK.countries(query),
    queryFn: () => tboApi.listTboCountries(query),
    enabled,
    staleTime: 60_000,
  });

export const useTboStatesList = (query: TboStateListQuery, enabled: boolean) =>
  useQuery({
    queryKey: QK.states(query),
    queryFn: () => tboApi.listTboStates(query),
    enabled,
    staleTime: 60_000,
  });

export const useTboCitiesList = (query: TboCityListQuery, enabled: boolean) =>
  useQuery({
    queryKey: QK.cities(query),
    queryFn: () => tboApi.listTboCities(query),
    enabled,
    staleTime: 60_000,
  });

/**
 * TBO city options for the City Master combobox, keyed by TBO country code +
 * an optional server-side search term `q` (the endpoint caps results, so search
 * is how an operator reaches cities beyond the first page).
 */
export const useTboCityOptions = (countryCode: string | null, q = '') =>
  useQuery({
    queryKey: QK.cityOptions(countryCode ?? '', q),
    queryFn: () => tboApi.listTboCityOptions(countryCode!, q || undefined),
    enabled: !!countryCode,
    staleTime: 5 * 60_000,
  });

/** Sync status: row counts + last synced timestamp (drives the once-per-DB gate). */
export const useTboStatus = () =>
  useQuery({ queryKey: ['tbo', 'status'], queryFn: tboApi.getTboStatus, staleTime: 30_000 });

export const useSyncTbo = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: tboApi.syncTbo,
    onSuccess: (res) => {
      qc.invalidateQueries({ queryKey: ['tbo'] });
      toast.success(
        `TBO synced: ${res.countries} countries, ${res.states} states, ${res.cities} cities`,
      );
    },
    onError: (err: unknown) => toast.error(extractMessage(err) ?? 'TBO sync failed'),
  });
};

/**
 * 2026-07-06 ADR-0059. TBO hotel mirror admin - reads + on-demand
 * sync from the Hotels tab on the TBO Geography page.
 *
 * The mirror is now also populated lazily by `POST /tbo-hotel/search`
 * (searches auto-backfill misses). The manual sync stays useful for
 * pre-warming markets before a launch or after a wholesale TBO
 * content refresh - see `docs/operations/tbo-hotel-mirror-sync.md`.
 */
export const useTboHotelsList = (query: TboHotelListQuery, enabled: boolean) =>
  useQuery({
    queryKey: QK.hotels(query),
    queryFn: () => tboApi.listTboHotels(query),
    enabled,
    staleTime: 30_000,
  });

export const useTboHotelStatus = () =>
  useQuery({
    queryKey: QK.hotelStatus(),
    queryFn: tboApi.getTboHotelStatus,
    staleTime: 30_000,
  });

export { useTboHotelSync } from './use-tbo-hotel-sync';

const extractMessage = (err: unknown): string | null => {
  if (err && typeof err === 'object') {
    const e = err as { message?: unknown; details?: { message?: unknown } };
    if (e.details && typeof e.details.message === 'string') return e.details.message;
    if (typeof e.message === 'string') return e.message;
  }
  if (err instanceof Error) return err.message;
  return null;
};
